import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, Edit3, FileSpreadsheet, Loader2 } from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Card } from "../../components/Card/Card";
import { DataTable, type Column } from "../../components/DataTable/DataTable";
import { FilterBar } from "../../components/FilterBar/FilterBar";
import { StatsDonut } from "../../components/StatsDonut/StatsDonut";
import { StatusPill } from "../../components/StatusPill/StatusPill";
import { TopNav } from "../../components/TopNav/TopNav";
import {
  type CaseRow,
  type CaseStats,
  type ExcelExport,
  api,
} from "../../lib/api";
import { formatDate, formatDateTime, formatEuro } from "../../lib/format";

import { CaseEditDialog } from "./CaseEditDialog";
import styles from "./CasosPage.module.css";

interface FileFilters {
  search: string;
  date: string;
  estado: string;
}

interface CaseFilters {
  search: string;
  date: string;
  estado: string;
  tipos: string[];
}

const EMPTY_FILE_FILTERS: FileFilters = { search: "", date: "", estado: "" };
const EMPTY_CASE_FILTERS: CaseFilters = { search: "", date: "", estado: "", tipos: [] };
const PAGE_SIZE = 100;

export default function CasosPage() {
  const navigate = useNavigate();

  const [stats, setStats] = useState<CaseStats | null>(null);
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [exports, setExports] = useState<ExcelExport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fileFilters, setFileFilters] = useState<FileFilters>(EMPTY_FILE_FILTERS);
  const [appliedFileFilters, setAppliedFileFilters] =
    useState<FileFilters>(EMPTY_FILE_FILTERS);
  const [caseFilters, setCaseFilters] = useState<CaseFilters>(EMPTY_CASE_FILTERS);
  const [appliedCaseFilters, setAppliedCaseFilters] =
    useState<CaseFilters>(EMPTY_CASE_FILTERS);

  const [tipoOptions, setTipoOptions] = useState<string[]>([]);

  const [editing, setEditing] = useState<CaseRow | null>(null);

  async function fetchCases(p: number, filters: CaseFilters) {
    const params = new URLSearchParams();
    params.set("page", String(p));
    params.set("page_size", String(PAGE_SIZE));
    params.set("ordering", "-created_at");
    if (filters.search.trim()) params.set("search", filters.search.trim());
    if (filters.estado) params.set("Estado", filters.estado);
    if (filters.tipos.length > 0) params.set("TipoDocto__in", filters.tipos.join(","));
    if (filters.date) params.set("created_at__date", filters.date);
    const c = await api.listCases(params);
    setCases(c.results);
    setTotalCount(c.count);
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [s, x, tipos] = await Promise.all([
        api.caseStats(),
        api.listExports(),
        api.caseTipoOptions(true),
      ]);
      setStats(s);
      setExports(x.results);
      setTipoOptions(tipos);
      await fetchCases(page, appliedCaseFilters);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível carregar os dados.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function applyFileFilters() {
    setAppliedFileFilters(fileFilters);
  }

  function clearFileFilters() {
    setFileFilters(EMPTY_FILE_FILTERS);
    setAppliedFileFilters(EMPTY_FILE_FILTERS);
  }

  async function applyCaseFilters() {
    setPage(1);
    setAppliedCaseFilters(caseFilters);
    setLoading(true);
    try {
      await fetchCases(1, caseFilters);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }

  async function clearCaseFilters() {
    const empty = EMPTY_CASE_FILTERS;
    setPage(1);
    setCaseFilters(empty);
    setAppliedCaseFilters(empty);
    setLoading(true);
    try {
      await fetchCases(1, empty);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }

  async function goToPage(p: number) {
    setPage(p);
    setLoading(true);
    try {
      await fetchCases(p, appliedCaseFilters);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const filteredExports = useMemo(() => {
    const f = appliedFileFilters;
    const q = f.search.trim().toLowerCase();
    return exports.filter((e) => {
      if (q && !e.filename.toLowerCase().includes(q)) return false;
      if (f.date && e.created_at?.slice(0, 10) !== f.date) return false;
      if (f.estado) {
        const status = e.download_url ? "available" : "pending";
        if (status !== f.estado) return false;
      }
      return true;
    });
  }, [exports, appliedFileFilters]);

  const exportColumns: Column<ExcelExport>[] = [
    {
      key: "filename",
      header: "Nome do Ficheiro",
      render: (row) => row.filename,
      sortable: true,
      sortValue: (row) => row.filename,
    },
    {
      key: "descricao",
      header: "Descrição",
      render: (row) => `${row.case_count} caso(s) exportado(s)`,
      sortable: true,
      sortValue: (row) => row.case_count,
    },
    {
      key: "data",
      header: "Gerado em",
      render: (row) => formatDateTime(row.created_at),
      sortable: true,
      sortValue: (row) => (row.created_at ? new Date(row.created_at) : null),
    },
    {
      key: "estado",
      header: "Estado",
      render: (row) =>
        row.download_url ? (
          <StatusPill tone="available">Disponível</StatusPill>
        ) : (
          <StatusPill tone="pending">A gerar</StatusPill>
        ),
      width: "110px",
      sortable: true,
      sortValue: (row) => (row.download_url ? "available" : "pending"),
    },
    {
      key: "acao",
      header: "Ações",
      render: (row) => (
        <div className={styles.rowActions}>
          {row.download_url ? (
            <button
              type="button"
              className={styles.iconLink}
              onClick={() => void api.downloadExport(row.id, row.filename)}
              aria-label="Descarregar"
            >
              <Download size={16} />
            </button>
          ) : (
            <span className={styles.iconLinkDisabled} aria-label="A gerar">
              <Loader2 size={16} className={styles.spin} />
            </span>
          )}
        </div>
      ),
      width: "70px",
    },
  ];

  const caseColumns: Column<CaseRow>[] = [
    {
      key: "cod",
      header: "Cod. Projeto",
      render: (row) => row.CodProjeto,
      sortable: true,
      sortValue: (row) => row.CodProjeto,
    },
    {
      key: "mov",
      header: "Mov. Num",
      render: (row) => `${String(row.MovNum).padStart(7, "0")}`,
      sortable: true,
      sortValue: (row) => row.MovNum,
    },
    {
      key: "nif",
      header: "NIF Destinatário",
      render: (row) => row.NIFDestinatario,
      sortable: true,
      sortValue: (row) => row.NIFDestinatario,
    },
    {
      key: "vat",
      header: "VAT Fornecedor",
      render: (row) => row.VATFornecedor || "",
      sortable: true,
      sortValue: (row) => row.VATFornecedor,
    },
    {
      key: "valor",
      header: "Valor Imputado",
      render: (row) => formatEuro(row.ValorImputado),
      sortable: true,
      sortValue: (row) => (row.ValorImputado ? Number(row.ValorImputado) : null),
    },
    {
      key: "data",
      header: "Data Documento",
      render: (row) => formatDate(row.DataDocto),
      sortable: true,
      sortValue: (row) => (row.DataDocto ? new Date(row.DataDocto) : null),
    },
    {
      key: "tipo",
      header: "Tipo",
      render: (row) => row.TipoDocto,
      sortable: true,
      sortValue: (row) => row.TipoDocto,
    },
    {
      key: "excel",
      header: "Excel",
      render: (row) => {
        const latest = row.exports?.[0];
        if (latest) {
          return (
            <div className={styles.excelCell}>
              <button
                type="button"
                className={styles.excelBtn}
                onClick={() => void api.downloadExport(latest.id, latest.filename)}
                aria-label={latest.filename}
              >
                <FileSpreadsheet size={16} />
              </button>
              <span className={styles.tooltip}>{latest.filename}</span>
            </div>
          );
        }
        return (
          <span style={{ color: '#aaa', display: 'flex', justifyContent: 'center' }}>
            -
          </span>
        );
      },
      width: "80px",
    },
    {
      key: "estado",
      header: "Estado",
      render: (row) => {
        switch (row.Estado) {
          case "Enviado":
            return <StatusPill tone="sent">Enviado</StatusPill>;
          case "Por Reenviar":
            return <StatusPill tone="resend">Por Reenviar</StatusPill>;
          default:
            return <StatusPill tone="to-send">Por Enviar</StatusPill>;
        }
      },
      width: "120px",
      sortable: true,
      sortValue: (row) => row.Estado,
    },
    {
      key: "acoes",
      header: "Ações",
      render: (row) => (
        <div className={styles.rowActions}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => setEditing(row)}
            aria-label="Editar caso"
          >
            <Edit3 size={14} />
          </button>
        </div>
      ),
      width: "70px",
    },
  ];

  return (
    <div className={styles.page}>
      <TopNav />

      <main className={styles.main}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.topGrid}>
          <Card title="Ficheiros Excel gerados">
            <FilterBar
              searchValue={fileFilters.search}
              onSearchChange={(v) => setFileFilters((f) => ({ ...f, search: v }))}
              dateValue={fileFilters.date}
              onDateChange={(v) => setFileFilters((f) => ({ ...f, date: v }))}
              estadoValue={fileFilters.estado}
              onEstadoChange={(v) => setFileFilters((f) => ({ ...f, estado: v }))}
              estadoOptions={[
                { value: "available", label: "Disponível" },
                { value: "pending", label: "A gerar" },
              ]}
              onApply={applyFileFilters}
              onClear={clearFileFilters}
              trailing={
                <Button variant="soft" onClick={() => navigate("/enviar")}>
                  Gerar EXCEL
                </Button>
              }
            />
            <DataTable
              columns={exportColumns}
              rows={filteredExports}
              rowKey={(r) => r.id}
              empty={loading ? "A carregar…" : "Sem ficheiros gerados."}
              maxHeight="190px"
            />
          </Card>

          <StatsDonut
            total={stats?.total ?? 0}
            enviados={stats?.enviado ?? 0}
            porEnviar={stats?.por_enviar ?? 0}
            porReenviar={stats?.por_reenviar ?? 0}
          />
        </div>

        <Card title="Tabela com casos criados">
          <FilterBar
            searchValue={caseFilters.search}
            onSearchChange={(v) => setCaseFilters((f) => ({ ...f, search: v }))}
            dateValue={caseFilters.date}
            onDateChange={(v) => setCaseFilters((f) => ({ ...f, date: v }))}
            estadoValue={caseFilters.estado}
            onEstadoChange={(v) => setCaseFilters((f) => ({ ...f, estado: v }))}
            estadoOptions={[
              { value: "Por Enviar", label: "Por Enviar" },
              { value: "Enviado", label: "Enviado" },
              { value: "Por Reenviar", label: "Por Reenviar" },
            ]}
            tipoValues={caseFilters.tipos}
            onTipoChange={(v) => setCaseFilters((f) => ({ ...f, tipos: v }))}
            tipoOptions={tipoOptions}
            tipoPlaceholder="Tipo de documento"
            onApply={() => void applyCaseFilters()}
            onClear={() => void clearCaseFilters()}
            trailing={
              <Button variant="soft" onClick={() => navigate("/consultar")}>
                Criar casos
              </Button>
            }
          />
          <DataTable
            columns={caseColumns}
            rows={cases}
            rowKey={(r) => r.id}
            empty={loading ? "A carregar…" : "Sem casos registados."}
            maxHeight="360px"
          />
          {totalCount > 0 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                onClick={() => void goToPage(page - 1)}
                disabled={page <= 1 || loading}
                aria-label="Página anterior"
              >
                <ChevronLeft size={16} />
              </button>
              <span className={styles.pageInfo}>
                Página {page} de {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                onClick={() => void goToPage(page + 1)}
                disabled={page >= totalPages || loading}
                aria-label="Próxima página"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </Card>
      </main>

      <CaseEditDialog
        open={editing !== null}
        caseRow={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          void refresh();
        }}
      />
    </div>
  );
}
